/**
 * 8-bit audio engine — original sounds, original compositions.
 * Uses Web Audio API square/triangle waves to produce
 * retro chiptune SFX (jingles, blips, error sounds).
 *
 * No copyrighted melodies or samples are used.
 */

type WaveType = OscillatorType;

let audioCtx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let muted = false;
let volume = 0.18;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!audioCtx) {
    try {
      const Ctx = (window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
      audioCtx = new Ctx();
      masterGain = audioCtx.createGain();
      masterGain.gain.value = volume;
      masterGain.connect(audioCtx.destination);
    } catch {
      return null;
    }
  }
  if (audioCtx.state === 'suspended') {
    void audioCtx.resume();
  }
  return audioCtx;
}

const VOL_KEY = 'vicin-gb-volume';
const MUTE_KEY = 'vicin-gb-muted';

function loadPrefs() {
  try {
    const v = localStorage.getItem(VOL_KEY);
    if (v !== null) volume = Math.max(0, Math.min(1, Number(v)));
    const m = localStorage.getItem(MUTE_KEY);
    if (m !== null) muted = m === '1';
  } catch { /* */ }
}
loadPrefs();

export function getMuted(): boolean { return muted; }
export function getVolume(): number { return volume; }

export function setMuted(value: boolean) {
  muted = value;
  if (masterGain) masterGain.gain.value = value ? 0 : volume;
  try { localStorage.setItem(MUTE_KEY, value ? '1' : '0'); } catch { /* */ }
}

export function setVolume(value: number) {
  volume = Math.max(0, Math.min(1, value));
  if (masterGain && !muted) masterGain.gain.value = volume;
  try { localStorage.setItem(VOL_KEY, String(volume)); } catch { /* */ }
}

/** Resume the audio context. Must be called from a user gesture. */
export function unlockAudio(): void {
  getCtx();
}

function tone(
  freq: number,
  durationMs: number,
  options: { wave?: WaveType; vol?: number; attack?: number; release?: number } = {},
) {
  if (muted) return;
  const ctx = getCtx();
  if (!ctx || !masterGain) return;
  const { wave = 'square', vol = 1, attack = 0.005, release = 0.02 } = options;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = wave;
  osc.frequency.value = freq;
  const t0 = ctx.currentTime;
  const t1 = t0 + durationMs / 1000;
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(vol, t0 + attack);
  gain.gain.setValueAtTime(vol, Math.max(t0 + attack, t1 - release));
  gain.gain.exponentialRampToValueAtTime(0.001, t1);
  osc.connect(gain);
  gain.connect(masterGain);
  osc.start(t0);
  osc.stop(t1 + 0.02);
}

function sweep(from: number, to: number, durationMs: number, options: { wave?: WaveType; vol?: number } = {}) {
  if (muted) return;
  const ctx = getCtx();
  if (!ctx || !masterGain) return;
  const { wave = 'square', vol = 1 } = options;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = wave;
  const t0 = ctx.currentTime;
  const t1 = t0 + durationMs / 1000;
  osc.frequency.setValueAtTime(from, t0);
  osc.frequency.exponentialRampToValueAtTime(to, t1);
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(vol, t0 + 0.005);
  gain.gain.exponentialRampToValueAtTime(0.001, t1);
  osc.connect(gain);
  gain.connect(masterGain);
  osc.start(t0);
  osc.stop(t1 + 0.02);
}

export const sfx = {
  /** Original 4-note jingle that plays on boot. */
  bootJingle() {
    const notes: Array<[number, number]> = [
      [392, 130],   // G4
      [523, 130],   // C5
      [659, 130],   // E5
      [784, 240],   // G5
    ];
    notes.forEach(([f, d], i) => setTimeout(() => tone(f, d, { vol: 0.8 }), i * 130));
  },

  /** Quick positive blip (select / navigate). */
  blip() {
    tone(880, 50, { vol: 0.7 });
  },

  /** Lower confirm (A button). */
  confirm() {
    tone(660, 60, { vol: 0.8 });
    setTimeout(() => tone(990, 80, { vol: 0.6 }), 60);
  },

  /** Cancel / back (B button). */
  cancel() {
    tone(440, 80, { vol: 0.6 });
    setTimeout(() => tone(330, 80, { vol: 0.5 }), 60);
  },

  /** Error buzz. */
  error() {
    sweep(440, 110, 280, { vol: 0.8, wave: 'square' });
  },

  /** Door open swoosh. */
  doorOpen() {
    sweep(300, 900, 380, { vol: 0.85, wave: 'square' });
    setTimeout(() => tone(1100, 60, { vol: 0.5, wave: 'triangle' }), 320);
  },

  /** Success fanfare — three ascending notes. */
  success() {
    const notes: Array<[number, number]> = [
      [523, 80],   // C5
      [659, 80],   // E5
      [784, 80],   // G5
      [1047, 180], // C6
    ];
    notes.forEach(([f, d], i) => setTimeout(() => tone(f, d, { vol: 0.75 }), i * 80));
  },

  /** Doorbell ring — two-tone chime. */
  doorbell() {
    tone(988, 120, { vol: 0.85, wave: 'triangle' });
    setTimeout(() => tone(1320, 280, { vol: 0.7, wave: 'triangle' }), 120);
  },

  /** Coin pickup (Mario-style — original 2-note blip). */
  coin() {
    tone(988, 50, { vol: 0.75 });
    setTimeout(() => tone(1319, 100, { vol: 0.7 }), 50);
  },

  /** Level up arpeggio. */
  levelUp() {
    const notes = [523, 659, 784, 1047, 1319, 1568];
    notes.forEach((f, i) => setTimeout(() => tone(f, 70, { vol: 0.7 }), i * 70));
  },

  /** Menu navigation tick. */
  tick() {
    tone(1200, 30, { vol: 0.5 });
  },
};
